import {FC, Fragment, ReactNode} from 'react'
import {Opportunity} from '@features/insights/types'
import {useTranslation} from '@/translations/i18n'
import {useTranslation as useRawTranslation} from 'react-i18next'
import {Lightbulb01Icon, LinkExternal01Icon} from '@components/ui/icon'
import {GUIDELINE_URLS, ISSUE_TO_GUIDELINE, PHOTOGRAPHER_RESOURCE_CENTER_URL} from '@features/insights/constants'
import {ScrollArea, Scrollbar} from '@components/ui/scroll-area'

interface ReviewFeedbackDefinitionProps {
    definitionKey: Opportunity['keyName']
    isMobile?: boolean
}

export const ReviewFeedbackDefinition: FC<ReviewFeedbackDefinitionProps> = ({definitionKey, isMobile}) => {
    const {t} = useTranslation()
    const {t: tRaw} = useRawTranslation()

    const fixSteps = tRaw(`insights:issues:improve:${definitionKey}`, {returnObjects: true}) as unknown as string[]
    const guidelineKey = ISSUE_TO_GUIDELINE[definitionKey]

    const Wrapper = ({children}: {children: ReactNode}) =>
        isMobile ? (
            <Fragment>{children}</Fragment>
        ) : (
            <ScrollArea className="h-full w-full min-h-0" scrollbar={<Scrollbar className="m-1" />}>
                {children}
            </ScrollArea>
        )

    return (
        <Wrapper>
            <div className="flex flex-col gap-4 p-6 md:pt-4 md:pr-[60px] md:pb-6 md:pl-6 [&_h4]:m-0 [&_h4]:font-medium [&_h4]:text-md [&_span]:text-neutral-600">
                <h4>{t('insights:issues:review_definition_title')}</h4>
                <span>{t(`insights:issues:reason_text:${definitionKey}`)}</span>

                <div className="flex flex-col gap-4 p-4 rounded-xl border border-success-300 bg-success-100">
                    <div className="flex gap-2.5 items-center [&_span]:text-success-600 [&_span]:font-bold [&_svg]:fill-success-600">
                        <Lightbulb01Icon />
                        <span>{t('insights:issues:how_to_improve')}</span>
                    </div>

                    <div className="h-full text-sm [&_a]:cursor-pointer [&_a]:underline [&_ol]:m-0 [&_ol]:pl-5 [&_ol]:flex [&_ol]:flex-col [&_ol]:gap-2 [&_li]:list-decimal">
                        <ol>
                            {Array.isArray(fixSteps) &&
                                fixSteps.map((step, index) => <li key={`${definitionKey}-fix-${index}`}>{step}</li>)}
                        </ol>
                    </div>
                </div>

                <h5 className="m-0 font-medium text-neutral-900 text-sm">{t('insights:issues:resources_title')}</h5>
                <div className="flex flex-col gap-4">
                    {guidelineKey && (
                        <a
                            className="inline-flex items-center gap-1.5 underline text-neutral-600 font-medium cursor-pointer text-sm [&_svg]:w-5 [&_svg]:h-5 [&_svg]:fill-neutral-600"
                            href={GUIDELINE_URLS[guidelineKey]}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <LinkExternal01Icon />
                            {t(`insights:guidelines:${guidelineKey}_title` as const)}
                        </a>
                    )}
                    <a
                        className="inline-flex items-center gap-1.5 underline text-neutral-600 font-medium cursor-pointer text-sm [&_svg]:w-5 [&_svg]:h-5 [&_svg]:fill-neutral-600"
                        href={PHOTOGRAPHER_RESOURCE_CENTER_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <LinkExternal01Icon />
                        {t('insights:issues:photographer_resource_center')}
                    </a>
                </div>
            </div>
        </Wrapper>
    )
}
